/**
 * Live element-drag state — one store per open board view, owned by
 * BoardCanvas. LOCAL view state only: the committed geometry lives in the Yjs
 * doc, and a peer's in-flight drag arrives through awareness (F-46/C9). This
 * store is the equivalent channel for OUR OWN in-flight drag, which awareness
 * deliberately does not echo back to us.
 *
 * Two readers need it, which is exactly why it is a store and not component
 * state:
 *
 *  - the F-34 GROUP drag — the element under the pointer publishes a rect for
 *    every element in the selection, and each follower renders its own entry
 *    instead of its committed geometry;
 *  - the F-38 connector layer — a line must ride the motion of the element it
 *    is attached to, on the dragging client too. Without this it would sit on
 *    the doc's stale geometry until release and then snap, which is precisely
 *    the "pins teleport on release" failure docs/04 §3 calls out.
 *
 * Held as a Map replaced wholesale per pointer frame: a component subscribed to
 * `live.get(myId)` re-renders only when ITS rect changed, so publishing the
 * whole selection costs one render per moved element, not one per element on
 * the board.
 *
 * The F-34 marquee box lives here for the same reason the live rects do: it
 * changes on every pointer frame, and holding it in BoardCanvas state would put
 * a re-render of the whole canvas shell — grid included — into every frame of a
 * selection drag. Only `MarqueeBox` subscribes to it.
 */
import { createStore, type StoreApi } from "zustand/vanilla";

import type { BoardRect } from "../../registries/toolbar-actions/index.js";

/** The two dragged corners of the marquee, in BOARD units. */
export interface MarqueeBox {
  from: { x: number; y: number };
  to: { x: number; y: number };
}

export interface ElementsUiState {
  /** Board rects of the elements this client is dragging/resizing right now. */
  live: ReadonlyMap<string, BoardRect>;
  /** The in-flight F-34 selection box, or null when none is being drawn. */
  marquee: MarqueeBox | null;
  setLive(rects: ReadonlyMap<string, BoardRect>): void;
  clearLive(): void;
  setMarquee(marquee: MarqueeBox | null): void;
}

export type ElementsUiStore = StoreApi<ElementsUiState>;

const EMPTY: ReadonlyMap<string, BoardRect> = new Map();

export function createElementsUiStore(): ElementsUiStore {
  return createStore<ElementsUiState>((set, get) => ({
    live: EMPTY,
    marquee: null,
    setLive: (rects) => set({ live: rects }),
    setMarquee: (marquee) => {
      if (marquee === null && get().marquee === null) return;
      set({ marquee });
    },
    clearLive: () => {
      // Guarded so releasing a plain click (no drag ever started) does not
      // publish a new empty Map and re-render every subscriber for nothing.
      if (get().live.size > 0) set({ live: EMPTY });
    },
  }));
}
